import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvent } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import echo from '../services/echo';
import './Map.css';
import { placeService } from '../services/api';
import PinForm from './PinForm';
import PlaceMarkers from './PlaceMarkers';
import SearchBar from './SearchBar';
import FilterBar from './FilterBar';
import { formatTimeAgo } from '../utils/dateUtils';
import { useTheme } from '../contexts/ThemeContext';

// Get Stadia Maps API key from environment variable
const STADIA_API_KEY = process.env.REACT_APP_STADIA_API_KEY || '';

// Map style configurations
const MAP_STYLES = {
  osm: {
    name: '🗺️ Classic',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    theme: 'light',
  },
  cartodb_light: {
    name: '☀️ Clean Light',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    theme: 'light',
  },
  cartodb_voyager: {
    name: '🌊 Voyager',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    theme: 'light',
  },
  stadia_outdoors: {
    name: '🏔️ Outdoors',
    url: `https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png${STADIA_API_KEY ? `?api_key=${STADIA_API_KEY}` : ''}`,
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
    theme: 'light',
  },
  opentopomap: {
    name: '🗻 Topographic',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
    theme: 'light',
  },
  stamen_watercolor: {
    name: '🎨 Watercolor',
    url: `https://tiles.stadiamaps.com/tiles/stamen_watercolor/{z}/{x}/{y}.jpg${STADIA_API_KEY ? `?api_key=${STADIA_API_KEY}` : ''}`,
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://stamen.com">Stamen Design</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
    theme: 'light',
  },
  esri_world: {
    name: '🛰️ Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    theme: 'light',
  },
  cartodb_dark: {
    name: '🌙 Dark Matter',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    theme: 'dark',
  },
  stadia_dark: {
    name: '🌑 Smooth Dark',
    url: `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png${STADIA_API_KEY ? `?api_key=${STADIA_API_KEY}` : ''}`,
    attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
    theme: 'dark',
  },
  cartodb_dark_nolabels: {
    name: '🌒 Dark (No Labels)',
    url: 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    theme: 'dark',
  },
};

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom anchor icon - red with star
const anchorIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="45" viewBox="0 0 32 45">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
          <feOffset dx="0" dy="2" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.3"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <path d="M16 0C9.4 0 4 5.4 4 12c0 8 12 33 12 33s12-25 12-33c0-6.6-5.4-12-12-12z" 
            fill="#DC2626" filter="url(#shadow)"/>
      <circle cx="16" cy="12" r="8" fill="#FEE2E2"/>
      <path d="M16 6 L18 11 L23 11 L19 14 L21 19 L16 16 L11 19 L13 14 L9 11 L14 11 Z" 
            fill="#DC2626"/>
    </svg>
  `),
  iconSize: [32, 45],
  iconAnchor: [16, 45],
  popupAnchor: [0, -45],
});

// Inner component to handle map clicks using useMapEvent hook
const MapClickHandler = ({ onMapClick, isStyleSelectorOpen, isFormOpen, isFilterOpen }) => {
  useMapEvent('click', (e) => {
    // Only trigger map click if style selector, form, and filter are not open
    if (!isStyleSelectorOpen && !isFormOpen && !isFilterOpen) {
      onMapClick(e);
    }
  });
  return null;
};

const Map = () => {
  const [places, setPlaces] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'nearby'
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [mapStyle, setMapStyle] = useState(() => {
    // Load saved style from localStorage
    return localStorage.getItem('mapStyle') || 'osm';
  });
  const [showStyleSelector, setShowStyleSelector] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showServerDownModal, setShowServerDownModal] = useState(() => {
    const hasDismissed = localStorage.getItem('serverDownModalDismissed');
    return hasDismissed !== 'true';
  });

  const { theme, toggleTheme } = useTheme();

  const dismissServerDownModal = () => {
    localStorage.setItem('serverDownModalDismissed', 'true');
    setShowServerDownModal(false);
  };

  // Vibe Filtering State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterMoods, setFilterMoods] = useState([]);
  const [filterStyles, setFilterStyles] = useState([]);

  const [clusteringEnabled, setClusteringEnabled] = useState(() => {
    // Load clustering preference from localStorage (default: true)
    const saved = localStorage.getItem('clusteringEnabled');
    return saved !== null ? saved === 'true' : true;
  });
  const mapRef = useRef(null);
  const markerRefs = useRef({});

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
          setAnchorPosition([latitude, longitude]);
          fetchAllPlaces();
        },
        (error) => {
          console.error('Geolocation error:', error);
          setUserLocation([37.7749, -122.4194]);
          setAnchorPosition([37.7749, -122.4194]);
          fetchAllPlaces();
        }
      );
    }
  }, []);

  // Real-time updates for new pins
  useEffect(() => {
    const channel = echo.channel('places');
    channel.listen('PinCreated', (e) => {
      // Add new place and re-sort
      setPlaces((prevPlaces) => {
        if (prevPlaces.some(p => p.id === e.place.id)) return prevPlaces;
        return [e.place, ...prevPlaces].sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
      });
    });

    return () => {
      echo.leave('places');
    };
  }, []);

  // Save map style to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('mapStyle', mapStyle);
  }, [mapStyle]);

  // Save clustering preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('clusteringEnabled', clusteringEnabled.toString());
  }, [clusteringEnabled]);

  // Close style selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStyleSelector && !event.target.closest('.style-selector-container')) {
        setShowStyleSelector(false);
      }
      // Also close filter if clicking outside
      if (isFilterOpen && !event.target.closest('.filter-container')) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStyleSelector, isFilterOpen]);

  const handleStyleChange = (styleKey) => {
    setMapStyle(styleKey);
    setShowStyleSelector(false);
  };

  // Memoize filtered style arrays to avoid recomputation on every render
  const lightStyles = useMemo(() =>
    Object.entries(MAP_STYLES).filter(([_, style]) => style.theme === 'light'),
    []
  );

  const darkStyles = useMemo(() =>
    Object.entries(MAP_STYLES).filter(([_, style]) => style.theme === 'dark'),
    []
  );

  const toggleViewMode = () => {
    if (viewMode === 'all') {
      setViewMode('nearby');
      if (anchorPosition) {
        fetchNearbyPlaces(anchorPosition[0], anchorPosition[1]);
      }
    } else {
      setViewMode('all');
      fetchAllPlaces();
    }
  };

  const handleRefresh = () => {
    if (viewMode === 'nearby' && anchorPosition) {
      fetchNearbyPlaces(anchorPosition[0], anchorPosition[1]);
    } else {
      fetchAllPlaces();
    }
  };

  const handleAnchorDragEnd = (event) => {
    const newPos = event.target.getLatLng();
    setAnchorPosition([newPos.lat, newPos.lng]);
    if (viewMode === 'nearby') {
      fetchNearbyPlaces(newPos.lat, newPos.lng);
    }
  };

  const fetchNearbyPlaces = async (lat, lng) => {
    try {
      setLoading(true);
      const data = await placeService.getNearby(lat, lng, 10);
      setPlaces(data.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0)));
    } catch (error) {
      console.error('Error fetching places:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPlaces = async () => {
    try {
      setLoading(true);
      const data = await placeService.getAll();
      setPlaces(data.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0)));
    } catch (error) {
      console.error('Error fetching all places:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    setSelectedLocation({ lat, lng });
    setShowForm(true);
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  const handleCenterMap = () => {
    if (mapRef.current && userLocation) {
      mapRef.current.setView(userLocation, 13, {
        animate: true,
      });
    }
  };

  const handlePlaceCreated = (newPlace) => {
    setPlaces([newPlace, ...places].sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0)));
    setShowForm(false);
    setSelectedLocation(null);
  };

  const handleLocationSelect = (location) => {
    if (mapRef.current) {
      mapRef.current.setView([location.lat, location.lng], 15, {
        animate: true,
      });
    }
  };

  // Wrap handlers in useCallback to ensure stable props for PlaceMarkers
  const handlePlaceDeleted = useCallback((deletedId) => {
    setPlaces((currentPlaces) => currentPlaces.filter((p) => p.id !== deletedId));
  }, []);

  const handlePlaceUpdated = useCallback((updatedPlace) => {
    setPlaces((currentPlaces) =>
      currentPlaces.map((p) => p.id === updatedPlace.id ? { ...p, ...updatedPlace } : p)
    );
  }, []);

  // Filter Logic
  const filteredPlaces = useMemo(() => {
    if (filterMoods.length === 0 && filterStyles.length === 0) {
      return places;
    }

    return places.filter((place) => {
      // Filter by Style
      if (filterStyles.length > 0) {
        if (!place.style || !filterStyles.includes(place.style)) {
          return false;
        }
      }

      // Filter by Mood
      if (filterMoods.length > 0) {
        const placeMoods = typeof place.moods === 'string' ? JSON.parse(place.moods) : (place.moods || []);
        // Check if place has AT LEAST ONE of the selected moods
        const hasMatchingMood = placeMoods.some(mood => filterMoods.includes(mood));
        if (!hasMatchingMood) {
          return false;
        }
      }

      return true;
    });
  }, [places, filterMoods, filterStyles]);

  if (!userLocation) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-700">Loading location...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 relative overflow-hidden">
      {/* Map Section - Left Side */}
      <div className="flex-1 relative h-full">
        <MapContainer
          center={userLocation}
          zoom={13}
          zoomControl={false}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          minZoom={2}  // Prevent extreme zoom out that would show multiple world copies
          maxBounds={[[-90, -180], [90, 180]]}  // Limit panning to one world copy
          maxBoundsViscosity={1.0}  // Make bounds solid (1.0 = hard boundary, 0.0 = soft)
          worldCopyJump={false}  // Disable world wrapping when panning
        >
          <MapClickHandler
            onMapClick={handleMapClick}
            isStyleSelectorOpen={showStyleSelector}
            isFormOpen={showForm}
            isFilterOpen={isFilterOpen}
          />
          <TileLayer
            url={MAP_STYLES[mapStyle].url}
            attribution={MAP_STYLES[mapStyle].attribution}
            noWrap={true}
          />

          {/* User location marker */}
          {userLocation && viewMode === 'all' && (
            <Marker position={userLocation}>
              <Popup>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 px-3 py-2 rounded-lg text-center border border-indigo-200">
                  <span className="text-indigo-900 font-medium">📍 Your location</span>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Draggable anchor marker (only in nearby mode) */}
          {viewMode === 'nearby' && anchorPosition && (
            <Marker
              position={anchorPosition}
              draggable={true}
              icon={anchorIcon}
              eventHandlers={{
                dragend: handleAnchorDragEnd,
              }}
            >
              <Popup>
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 px-4 py-3 rounded-xl text-center border border-indigo-200">
                  <div className="text-indigo-900 font-semibold mb-1">⚓ Anchor Pin</div>
                  <p className="text-xs text-indigo-700">Drag to move radius</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Place markers - Extracted to prevent re-renders */}
          <PlaceMarkers
            places={filteredPlaces}
            clusteringEnabled={clusteringEnabled}
            markerRefs={markerRefs}
            onDeleted={handlePlaceDeleted}
            onUpdated={handlePlaceUpdated}
          />
        </MapContainer>

        {showSearch && (
          <SearchBar
            onLocationSelect={handleLocationSelect}
            onClose={() => setShowSearch(false)}
          />
        )}

        {/* Header Overlay */}
        <div className="absolute top-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-b from-black/60 via-black/20 to-transparent pointer-events-none z-[1000]">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <h1 className="text-white text-3xl md:text-5xl font-extralight tracking-wider drop-shadow-lg">ElseWhere</h1>
              <p className="text-white/90 text-xs md:text-sm font-light tracking-wide hidden md:block">Leave marks on the map</p>
            </div>

            {/* Vertical icon-only button stack */}
            <div className="flex flex-col gap-2 items-end">
              {/* Burger Menu Button */}
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="pointer-events-auto p-2.5 bg-white/95 hover:bg-white text-gray-900 dark:bg-gray-800/95 dark:hover:bg-gray-800 dark:text-white rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700"
                title="Open menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              </button>

              {/* Filter Button & Component */}
              <div className="relative filter-container pointer-events-auto">
                <FilterBar
                  selectedMoods={filterMoods}
                  setSelectedMoods={setFilterMoods}
                  selectedStyles={filterStyles}
                  setSelectedStyles={setFilterStyles}
                  isOpen={isFilterOpen}
                  setIsOpen={setIsFilterOpen}
                />
              </div>

              {/* Search Button */}
              <button
                onClick={() => setShowSearch(true)}
                className="group pointer-events-auto flex items-center bg-white/95 hover:bg-white text-gray-900 dark:bg-gray-800/95 dark:hover:bg-gray-800 dark:text-white rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700 overflow-hidden"
                title="Search places"
              >
                <div className="p-2.5">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <span className="max-w-0 group-hover:max-w-xs pr-0 group-hover:pr-3 pl-0 group-hover:pl-1 opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap font-medium text-sm">
                  Search
                </span>
              </button>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="group pointer-events-auto flex items-center bg-white/95 hover:bg-white text-gray-900 dark:bg-gray-800/95 dark:hover:bg-gray-800 dark:text-white rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                title="Refresh pins"
              >
                <div className="p-2.5">
                  <svg
                    className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </div>
                <span className="max-w-0 group-hover:max-w-xs pr-0 group-hover:pr-3 pl-0 group-hover:pl-1 opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap font-medium text-sm">
                  Refresh
                </span>
              </button>

              {/* Clustering Toggle Button */}
              <button
                onClick={() => setClusteringEnabled(!clusteringEnabled)}
                className="group pointer-events-auto flex items-center bg-white/95 hover:bg-white text-gray-900 dark:bg-gray-800/95 dark:hover:bg-gray-800 dark:text-white rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700 overflow-hidden"
                title={clusteringEnabled ? 'Disable clustering' : 'Enable clustering'}
              >
                <div className="p-2.5">
                  {clusteringEnabled ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </div>
                <span className="max-w-0 group-hover:max-w-xs pr-0 group-hover:pr-3 pl-0 group-hover:pl-1 opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap font-medium text-sm">
                  {clusteringEnabled ? 'Clustering' : 'Individual'}
                </span>
              </button>

              {/* View Mode Toggle Button */}
              <button
                onClick={toggleViewMode}
                className="group pointer-events-auto flex items-center bg-white/95 hover:bg-white text-gray-900 dark:bg-gray-800/95 dark:hover:bg-gray-800 dark:text-white rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700 overflow-hidden"
                title={viewMode === 'all' ? 'Switch to nearby mode' : 'Switch to all pins'}
              >
                <div className="p-2.5">
                  {viewMode === 'all' ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      <circle cx="12" cy="12" r="3" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <span className="max-w-0 group-hover:max-w-xs pr-0 group-hover:pr-3 pl-0 group-hover:pl-1 opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap font-medium text-sm">
                  {viewMode === 'all' ? 'Nearby' : 'All Pins'}
                </span>
              </button>

              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className="group pointer-events-auto flex items-center bg-white/95 hover:bg-white text-gray-900 dark:bg-gray-800/95 dark:hover:bg-gray-800 dark:text-white rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700 overflow-hidden"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                <div className="p-2.5">
                  {theme === 'dark' ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </div>
                <span className="max-w-0 group-hover:max-w-xs pr-0 group-hover:pr-3 pl-0 group-hover:pl-1 opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap font-medium text-sm">
                  {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="absolute top-20 right-6 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 text-sm shadow-lg z-[1000]">
            <span className="animate-pulse">Loading nearby marks...</span>
          </div>
        )}

        {/* Ko-Fi Support Button - Container has safe-bottom, not the button */}
        <div className="absolute bottom-12 left-4 md:bottom-8 md:left-6 z-[1000] safe-bottom">
          <a
            href="https://ko-fi.com/dilligafm8"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white/90 hover:bg-white text-gray-900 dark:bg-gray-800/90 dark:hover:bg-gray-800 dark:text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center gap-2 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700 font-medium text-sm group"
          >
            <span className="group-hover:animate-bounce">☕</span>
            <span>Support me</span>
          </a>
        </div>

        <div className="absolute bottom-12 right-4 md:bottom-8 md:right-6 z-[1000] flex flex-col gap-3 safe-bottom">
          {/* Map Style Selector Button */}
          <div className="relative style-selector-container">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowStyleSelector(!showStyleSelector);
              }}
              className="p-2 bg-white/95 hover:bg-white text-gray-900 dark:bg-gray-800/95 dark:hover:bg-gray-800 dark:text-white rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700"
              title="Change map style"
              aria-label="Change map style"
              aria-expanded={showStyleSelector}
              aria-haspopup="true"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </button>

            {/* Style Selector Dropdown */}
            {showStyleSelector && (
              <>
                {/* Backdrop overlay */}
                <div
                  className="fixed inset-0 z-[1001]"
                  onClick={() => setShowStyleSelector(false)}
                  aria-hidden="true"
                />

                {/* Dropdown with higher z-index and click stop */}
                <div
                  className="absolute bottom-full right-0 mb-2 w-64 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-2xl border border-white/20 dark:border-gray-700 p-3 z-[1002]"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowStyleSelector(false);
                    }
                  }}
                  role="menu"
                  aria-label="Map style options"
                >
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 px-2">Map Style</h3>
                  <div className="space-y-1">
                    {/* Light Themes */}
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1">Light Modes</div>
                    {lightStyles.map(([key, style]) => (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleStyleChange(key);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 pointer-events-auto ${mapStyle === key
                          ? 'bg-indigo-100 text-indigo-900 font-medium dark:bg-indigo-900/50 dark:text-indigo-200'
                          : 'hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        role="menuitemradio"
                        aria-checked={mapStyle === key}
                      >
                        {style.name}
                      </button>
                    ))}

                    {/* Dark Themes */}
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1 pt-2">Dark Modes</div>
                    {darkStyles.map(([key, style]) => (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleStyleChange(key);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 pointer-events-auto ${mapStyle === key
                          ? 'bg-indigo-100 text-indigo-900 font-medium dark:bg-indigo-900/50 dark:text-indigo-200'
                          : 'hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        role="menuitemradio"
                        aria-checked={mapStyle === key}
                      >
                        {style.name}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          {/* Center Map Button */}
          <button
            onClick={handleCenterMap}
            className="p-2 bg-white/95 hover:bg-white text-gray-900 dark:bg-gray-800/95 dark:hover:bg-gray-800 dark:text-white rounded-xl shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border border-white/20 dark:border-gray-700"
            title="Center map on my location"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          {/* Zoom Controls */}
          <div className="flex flex-col bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-2xl border border-white/20 dark:border-gray-700 overflow-hidden">
            <button
              onClick={handleZoomIn}
              className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white transition-colors border-b border-gray-100 dark:border-gray-700"
              title="Zoom in"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
            <button
              onClick={handleZoomOut}
              className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white transition-colors"
              title="Zoom out"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar Overlay Backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[1999] backdrop-blur-sm"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Right Side */}
      <div className={`
        fixed inset-y-0 right-0 
        w-80 md:w-96 bg-white dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-100 dark:border-gray-800
        z-[2000]
        transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 relative">

          {/* Close Button - Mobile Only */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <h2 className="text-3xl font-light text-gray-900 dark:text-white mb-1">
            {viewMode === 'all' ? 'All Marks' : 'Nearby Marks'}
          </h2>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              {viewMode === 'all'
                ? `${places.length} marks worldwide`
                : `${places.length} within 10km radius`}
            </p>
          </div>
        </div>

        {/* Places List */}
        <div className="flex-1 overflow-y-auto">
          {places.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
              </div>
              <p className="text-gray-600 text-base font-medium mb-2">No marks found</p>
              <p className="text-gray-400 text-sm">Click anywhere on the map to leave your first mark!</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {places.map((place, index) => (
                <div
                  key={place.id}
                  className="p-4 bg-white dark:bg-gray-800 hover:bg-gradient-to-br hover:from-white hover:to-indigo-50 dark:hover:from-gray-800 dark:hover:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-indigo-200 dark:hover:border-indigo-900 hover:shadow-lg transition-all duration-300 cursor-pointer group transform hover:-translate-y-0.5"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => {
                    if (mapRef.current) {
                      mapRef.current.setView([place.lat, place.lng], 15);
                      // Close sidebar after selecting a place so we can see the map
                      setIsSidebarOpen(false);
                      // Open the popup after a short delay to ensure map has centered
                      setTimeout(() => {
                        const marker = markerRefs.current[place.id];
                        if (marker) {
                          marker.openPopup();
                        }
                      }, 100);
                    }
                  }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                        {formatTimeAgo(place.created_at)}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center gap-1 bg-pink-50 text-pink-600 px-2 py-0.5 rounded-md border border-pink-100 text-xs font-bold">
                          <span>♥</span>
                          <span>{place.likes_count || 0}</span>
                        </div>
                        {(place.likes_count || 0) >= 10 && (
                          <div className="flex items-center gap-1 bg-gradient-to-r from-blue-400 to-indigo-500 text-white px-1.5 py-0.5 rounded text-[10px] font-bold shadow-sm">
                            <span>🚀</span><span>Rising</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlaceDeleted(place.id);
                      }}
                      className="text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full w-6 h-6 flex items-center justify-center transition-all text-sm"
                    >
                      ✕
                    </button>
                  </div>

                  {place.moods && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(typeof place.moods === 'string' ? JSON.parse(place.moods) : place.moods)
                        .slice(0, 3)
                        .map((mood, i) => (
                          <span
                            key={mood}
                            className="inline-block bg-indigo-50 text-indigo-700 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border border-indigo-100"
                            style={{ animationDelay: `${i * 100}ms` }}
                          >
                            {mood}
                          </span>
                        ))}
                    </div>
                  )}

                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 mb-3 leading-relaxed font-normal">
                    {place.description}
                  </p>

                  <div className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-900/50 p-2 rounded-lg border border-gray-100 dark:border-gray-700">
                    <span className="text-gray-400 font-mono">
                      📍 {parseFloat(place.lat).toFixed(4)}, {parseFloat(place.lng).toFixed(4)}
                    </span>
                    <div className="flex items-center gap-1 text-gray-400">
                      <span>💬 {place.comments_count || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Server Down Notice Modal */}
      {showServerDownModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
          onClick={dismissServerDownModal}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-md w-full overflow-hidden animate-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Gradient header */}
            <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-6 py-5">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🛠️</span>
                <div>
                  <h2 className="text-white font-bold text-xl">Server Offline</h2>
                  <p className="text-white/80 text-sm">ElseWhere is partially unavailable</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">
                The backend server is currently offline due to hosting costs. You can still browse
                the map, but{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  adding pins and live features won't be available
                </span>{' '}
                until the server is back up.
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-5">
                If you enjoy ElseWhere and want to help keep it alive, consider supporting on
                Ko-Fi — every little bit helps! ☕
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://ko-fi.com/dilligafm8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-orange-400 to-pink-500 hover:from-orange-500 hover:to-pink-600 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  onClick={dismissServerDownModal}
                >
                  <span>☕</span>
                  <span>Support on Ko-Fi</span>
                </a>
                <button
                  onClick={dismissServerDownModal}
                  className="sm:flex-none px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm transition-all duration-200"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && selectedLocation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <PinForm
            location={selectedLocation}
            onCreated={handlePlaceCreated}
            onClose={() => {
              setShowForm(false);
              setSelectedLocation(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Map;
